import * as Yup from 'yup';
import type { FC } from 'react';
import { Formik, useFormikContext } from 'formik';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useSearchParams } from "react-router-dom";


import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Checkbox,
  Typography,
  Link,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { API, graphqlOperation } from '@aws-amplify/api';
import useAuth from 'src/hooks/useAuth';
import useRefMounted from 'src/hooks/useRefMounted';
import { useTranslation } from 'react-i18next';
import { PatternFormat } from 'react-number-format';
import parsePhoneNumber from 'libphonenumber-js';
import { clientOrdersFiltered } from 'src/graphql/queries';
import type { ClientOrdersFilteredQuery } from 'src/API';

const NON_US_POSTAL_CODE_HINT =
  'If the card used to place your order has a non-US billing postal code, use your ORDER ID instead.';
const ORDER_ID_CREDENTIAL_PATTERN = /^[a-f0-9]{8}$/i;
const BILLING_ZIP_CREDENTIAL_PATTERN = /^\d{5}(-\d{4})?$/;

function isOrderIdCredential(value: string): boolean {
  return ORDER_ID_CREDENTIAL_PATTERN.test(value.trim());
}

function isBillingZipCredential(value: string): boolean {
  return BILLING_ZIP_CREDENTIAL_PATTERN.test(value.trim());
}

function normalizeLoginCredential(value: string): string {
  const trimmed = value.trim();
  if (isOrderIdCredential(trimmed)) {
    return trimmed.toLowerCase();
  }
  return trimmed;
}

const LoginAmplify: FC = () => {
  const { login } = useAuth() as any;
  const isMountedRef = useRefMounted();
  const { t }: { t: any } = useTranslation();
  const navigate = useNavigate()
  const phoneRegExp = /^[+]?(1\-|1\s|1|\d{3}\-|\d{3}\s|)?((\(\d{3}\))|\d{3})(\-|\s)?(\d{3})(\-|\s)?(\d{4})$/g
  const [searchParams] = useSearchParams();
  const phoneParam = searchParams.has('phone') ? searchParams.get('phone') : ''
  const email = searchParams.has('email') ? searchParams.get('email') : ''
  const orderId = searchParams.has('orderid') ? searchParams.get('orderid') : ''
  const autoSubmit = searchParams.has('autosubmit') ? searchParams.get('autosubmit') : ''

  const parsedPhone = parsePhoneNumber(phoneParam)
  var phone = ''
  if (parsedPhone) {
    phone = parsedPhone.nationalNumber
  }

  // If this is coming in as url params from a link, auto submit the form
  const AutoSubmit = () => {
    const { submitForm } = useFormikContext();
    useEffect(() => {
      if (autoSubmit === 'true') {
        submitForm();
      }
    }, [submitForm]);
    return null;
  }


  return (
    <Formik
      initialValues={{
        // phone: phone,
        email: email,
        password: orderId,
        terms: true,
        submit: null
      }}
      validationSchema={Yup.object().shape({
        // phone: Yup.string()
        //   .matches(phoneRegExp, t('The phone number provided should be a valid phone number'))
        //   .max(255)
        //   .required(t('The phone number is required')),
        email: Yup.string()
          .max(255)
          .required(t('The email is required')),
        password: Yup.string()
          .max(255)
          .required(t('An order ID or billing ZIP code is required'))
          .test(
            'order-id-or-billing-zip',
            t('Enter a valid 8-character order ID or US billing ZIP code'),
            (value) => {
              if (!value) {
                return false;
              }
              const trimmed = value.trim();
              return (
                isOrderIdCredential(trimmed) || isBillingZipCredential(trimmed)
              );
            }
          ),
      })}
      onSubmit={async (
        values,
        { setErrors, setStatus, setSubmitting }
      ): Promise<void> => {
        try {
          const credential = normalizeLoginCredential(values.password);
          await login(values.email, credential);

          if (isMountedRef.current) {
            setStatus({ success: true });
            setSubmitting(false);

            if (isOrderIdCredential(credential)) {
              navigate('/resort-grocery/manage/orders/list/' + credential);
              return;
            }

            const response = (await API.graphql({
              ...graphqlOperation(clientOrdersFiltered, { count: 2, reverse: true }),
              authMode: 'AMAZON_COGNITO_USER_POOLS'
            })) as { data: ClientOrdersFilteredQuery };
            const orders = response.data.clientOrdersFiltered?.orders ?? [];
            const nextToken = response.data.clientOrdersFiltered?.nextToken;

            if (orders.length === 1 && !nextToken) {
              const order = orders[0];
              navigate(
                `/resort-grocery/manage/orders/single/${order.orderTimestamp}_${order.id}`
              );
              return;
            }

            navigate('/resort-grocery/manage/orders/list');
          }
        } catch (err) {
          console.error(err);
          if (isMountedRef.current) {
            setStatus({ success: false });
            if (err.code === 'UserNotFoundException') {
              setErrors({ email: "No orders exist for this email. Please check this is the email used to place your order."})
            } else if (err.message.includes('permission')) {  // TODO -hack until we can add codes to errors
              setErrors({ email: err.message });
            } else if (err.code === 'NotAuthorizedClientException') {
              setErrors({ email: err.message})
            } else {
              setErrors({ password: err.message });
            }
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue
      }): JSX.Element => (
        <form noValidate onSubmit={handleSubmit}>
          {/* <PatternFormat
            format="(###) ###-####"
            customInput={TextField}
            error={Boolean(touched.phone && errors.phone)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.phone && errors.phone}
            label={t('Phone number')}
            name="phone"
            onBlur={handleBlur}
            onValueChange={values => {
              const { formattedValue, value } = values
              setFieldValue('phone', value)
            }}
            // onValueChange={values => {
            //   handleChange({
            //     target: {
            //       name: "phone",  // TODO - I'm kind of hacking this in here, need to learn how this should properly work
            //       value: values.value
            //     }
            //   });
            // }}  
            type="tel"
            value={values.phone}
            variant="outlined"
            valueIsNumericString={true}  // this is required to make this work for initial values
          /> */}
          <TextField
            inputProps={{
              autoCapitalize: 'none', // stop safari mobile for auto capitalizeing the first letter
              autoCorrect: 'off'
            }}
            error={Boolean(touched.email && errors.email)}
            fullWidth
            autoFocus
            margin="normal"
            helperText={touched.email && errors.email}
            label={t('Email')}
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            value={values.email}
            variant="outlined"
          />
          <TextField
            inputProps={{
              autoCapitalize: 'none', // stop safari mobile for auto capitalizeing the first letter
              autoCorrect: 'off'
            }}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            margin="normal"
            helperText={
              touched.password && errors.password ? (
                <>
                  {errors.password}
                  <br />
                  {t(NON_US_POSTAL_CODE_HINT)}
                </>
              ) : undefined
            }
            label={t('Order ID or Billing ZIP Code')}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            //type="password"
            value={values.password}
            variant="outlined"
          />
          <Box
            alignItems="center"
            display={{ xs: 'block', md: 'flex' }}
            justifyContent="space-between"
          >
            <Box display={{ xs: 'block', md: 'flex' }}>
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={values.terms}
                    name="terms"
                    color="primary"
                    onChange={handleChange}
                  />
                }
                label={
                  <>
                    <Typography variant="body2">
                      {t('I accept the')}{' '}
                      <Link component="a" href="#">
                        {t('terms and conditions')}
                      </Link>
                      .
                    </Typography>
                  </>
                }
              /> */}
            </Box>
            {/* <Link component={RouterLink} to="/account/recover-password">
              <b>{t('Forgot password?')}</b>
            </Link> */}
          </Box>

          {Boolean(touched.terms && errors.terms) && (
            <FormHelperText error>{errors.terms}</FormHelperText>
          )}

          <Button
            sx={{
              mt: 3
            }}
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
            disabled={isSubmitting}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
          >
            {t('Access Order')}
          </Button>
          <AutoSubmit />
        </form>
      )}
    </Formik>
  );
};

export default LoginAmplify;
